import type { ReportePropuesta, ReporteProveedor } from '@adelante/shared';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';

/**
 * Logo oficial de Adelante (mismos polígonos y colores que `LogoAdelante`, viewBox 745×326).
 * Se dibuja como vectores dentro del PDF: no depende del navegador (canvas) y sale nítido.
 */
const LOGO_ANCHO = 745;
const LOGO_ALTO = 326;
const LOGO_FORMAS: { color: [number, number, number]; puntos: [number, number][] }[] = [
  // Sombra (verde oscuro #88A024), atrás
  {
    color: [136, 160, 36],
    puntos: [
      [541.795, 94.8125],
      [331.851, 94.8125],
      [189.631, 325.073],
      [402.984, 325.073],
    ],
  },
  {
    color: [136, 160, 36],
    puntos: [
      [744.966, 209.943],
      [535.022, 209.943],
      [467.299, 325.073],
      [677.242, 325.073],
    ],
  },
  // Primer plano (verde #ADD010)
  {
    color: [173, 208, 16],
    puntos: [
      [0, 0],
      [208.863, 0],
      [402.138, 325.074],
      [189.626, 325.074],
    ],
  },
  {
    color: [173, 208, 16],
    puntos: [
      [331.846, 94.8132],
      [541.79, 94.8132],
      [677.237, 325.074],
      [467.907, 325.074],
    ],
  },
];

/** Dibuja el logo con su esquina superior izquierda en (x, y) y la altura indicada (en pt). */
function dibujarLogo(doc: jsPDF, x: number, y: number, altura: number): number {
  const k = altura / LOGO_ALTO;
  for (const forma of LOGO_FORMAS) {
    const [primero, ...resto] = forma.puntos.map(([px, py]) => [x + px * k, y + py * k] as const);
    if (!primero) continue;
    const deltas: [number, number][] = [];
    let [cx, cy] = primero;
    for (const [px, py] of resto) {
      deltas.push([px - cx, py - cy]);
      cx = px;
      cy = py;
    }
    doc.setFillColor(...forma.color);
    doc.lines(deltas, primero[0], primero[1], [1, 1], 'F', true);
  }
  return LOGO_ANCHO * k;
}

const NEGRO = [30, 30, 30] as const;
const GRIS = [110, 110, 110] as const;
const VERDE = [136, 160, 36] as const;

function monto(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' (sin pasar por Date para no correr el día por la zona horaria). */
function fecha(iso: string | null): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

function moneda(p: ReporteProveedor): string {
  // Fuentes estándar del PDF no traen el símbolo ₡: se usa el código de la moneda.
  return p.currencyCode && p.currencyCode !== 'CRC' ? p.currencyCode : 'CRC';
}

/** Una hoja (página) del reporte: pago a un proveedor desde una cuenta de banco. */
function dibujarHoja(doc: jsPDF, reporte: ReportePropuesta, p: ReporteProveedor): void {
  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  const margen = 40;

  dibujarLogo(doc, margen, 30, 38);
  doc.setTextColor(...NEGRO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Detalle de pago a proveedor', ancho - margen, 48, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRIS);
  doc.text(`Fecha de pago: ${fecha(reporte.fechaPago)}`, ancho - margen, 63, { align: 'right' });

  doc.setDrawColor(...VERDE);
  doc.setLineWidth(1.5);
  doc.line(margen, 78, ancho - margen, 78);

  // Datos del pago
  const datos: [string, string][] = [
    ['Proveedor', `${p.vendorName ?? p.vendorCode} (${p.vendorCode})`],
    ['Cuenta de origen', p.cuentaBancoNombre ?? p.cuentaBancoOrigen ?? '—'],
    ['Moneda', moneda(p)],
  ];
  if (p.consecutivo !== null) datos.push(['Referencia de pago', String(p.consecutivo)]);
  let y = 100;
  for (const [k, v] of datos) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GRIS);
    doc.text(k, margen, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...NEGRO);
    doc.text(v, margen + 110, y);
    y += 16;
  }

  const m = moneda(p);
  autoTable(doc, {
    startY: y + 10,
    margin: { left: margen, right: margen, bottom: 60 },
    head: [
      [
        'Factura proveedor',
        'N.º interno',
        'Fecha factura',
        'Vencimiento',
        `Monto factura (${m})`,
        `Pagado (${m})`,
        `Saldo restante (${m})`,
      ],
    ],
    body: p.facturas.map((f) => [
      f.externalDocumentNo || '—',
      f.reemplazaA
        ? `${f.documentNo ?? '—'}\n(pago aplicado en lugar de ${f.reemplazaA})`
        : f.aplicadaADocumento
          ? `${f.documentNo ?? '—'}\nPago aplicado a ${f.aplicadaADocumento}`
          : (f.documentNo ?? '—'),
      fecha(f.postingDate),
      fecha(f.dueDate),
      monto(f.montoFactura),
      monto(f.montoPagado),
      monto(f.saldoRestante),
    ]),
    foot: [['', '', '', '', 'Total pagado', monto(p.totalPagado), '']],
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4, textColor: [...NEGRO] },
    headStyles: { fillColor: [...VERDE], textColor: 255, fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: [240, 244, 220], textColor: [...NEGRO], fontStyle: 'bold' },
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
  });

  // Pie de página
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  doc.text(
    `Propuesta "${reporte.nombre}" validada el ${fecha(reporte.validadaEn)}. Los montos corresponden a lo aprobado para este pago.`,
    margen,
    alto - 36,
  );
  doc.text('Adelante', ancho - margen, alto - 36, { align: 'right' });
}

/** Arma un PDF con una página por cada hoja (proveedor + cuenta) indicada. */
export function generarPdfReporte(reporte: ReportePropuesta, hojas: ReporteProveedor[]): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  hojas.forEach((p, i) => {
    if (i > 0) doc.addPage();
    dibujarHoja(doc, reporte, p);
  });
  return doc;
}

/** Nombre de archivo seguro (sin tildes ni símbolos) con la extensión indicada. */
function nombreArchivo(base: string, extension = 'pdf'): string {
  return `${base
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}.${extension}`;
}

function nombrePdfProveedor(reporte: ReportePropuesta, p: ReporteProveedor): string {
  return nombreArchivo(
    `Pago_${p.vendorName ?? p.vendorCode}_${reporte.fechaPago ?? reporte.idPropuesta}`,
  );
}

export async function descargarReporteProveedor(
  reporte: ReportePropuesta,
  p: ReporteProveedor,
): Promise<void> {
  generarPdfReporte(reporte, [p]).save(nombrePdfProveedor(reporte, p));
}

/** Un PDF por proveedor, empacados en un ZIP — listo para enviarle a cada proveedor el suyo. */
export async function descargarReporteZip(reporte: ReportePropuesta): Promise<void> {
  const zip = new JSZip();
  const usados = new Set<string>();
  for (const p of reporte.proveedores) {
    let nombre = nombrePdfProveedor(reporte, p);
    // Un proveedor pagado desde dos cuentas genera dos hojas: se distinguen por la cuenta.
    if (usados.has(nombre)) {
      nombre = nombreArchivo(
        `Pago_${p.vendorName ?? p.vendorCode}_${p.cuentaBancoOrigen ?? 'cuenta'}_${reporte.fechaPago ?? reporte.idPropuesta}`,
      );
    }
    usados.add(nombre);
    zip.file(nombre, generarPdfReporte(reporte, [p]).output('arraybuffer'));
  }
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombreArchivo(
    `Pagos_proveedores_${reporte.fechaPago ?? reporte.idPropuesta}`,
    'zip',
  );
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}
